/**
 * Store for managing inventory items
 *
 * Provides CRUD operations for inventory items like traffic wands,
 * with assignment tracking to members.
 */
import type { Timestamp } from 'firebase/firestore'
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import type {
  AssignInventoryItemInput,
  CreateInventoryItemInput,
  InventoryAssignment,
  InventoryItem,
  InventoryItemFirestoreData,
  InventoryStatus,
  ReturnInventoryItemInput,
} from '~/shared/types/inventory.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, liveDocListener, liveQueryListener } from '~/utils/liveData'

export const useInventoryStore = defineStore('inventory', {
  state: () => ({
    // Store items by document path for O(1) lookups
    items: {} as Record<string, InventoryItem>,
    loading: false,
    loaded: false,
  }),

  getters: {
    // Convert map to array for components
    itemsArray(): InventoryItem[] {
      return Object.values(this.items)
    },

    // Get available items
    availableItems(): InventoryItem[] {
      return this.itemsArray.filter(i => i.status === 'available')
    },

    // Get assigned items
    assignedItems(): InventoryItem[] {
      return this.itemsArray.filter(i => i.status === 'assigned')
    },

    // Get items assigned to a specific member
    itemsByMemberId(): (memberId: string) => InventoryItem[] {
      return (memberId: string) => {
        return this.itemsArray.filter(
          i => i.status === 'assigned' && i.currentAssignment?.memberId === memberId,
        )
      }
    },
  },

  actions: {
    /**
     * Convert Firestore document data to InventoryItem
     */
    convertToItem(docId: string, data: InventoryItemFirestoreData): InventoryItem {
      return {
        id: docId,
        category: data.category,
        name: data.name,
        description: data.description,
        serialNumber: data.serialNumber,
        status: data.status,
        created_at: (data.created_at as Timestamp)?.toDate?.() ?? new Date(),
        updated_at: (data.updated_at as Timestamp)?.toDate?.() ?? new Date(),
        createdBy: data.createdBy,
        createdByName: data.createdByName,
        currentAssignment: data.currentAssignment
          ? {
            ...data.currentAssignment,
            assignedAt: (data.currentAssignment.assignedAt as Timestamp)?.toDate?.()
              ?? data.currentAssignment.assignedAt,
          }
          : null,
        assignmentHistory: data.assignmentHistory?.map(h => ({
          ...h,
          assignedAt: (h.assignedAt as Timestamp)?.toDate?.() ?? h.assignedAt,
          returnedAt: h.returnedAt
            ? ((h.returnedAt as Timestamp)?.toDate?.() ?? h.returnedAt)
            : null,
        })),
        notes: data.notes,
      }
    },

    /**
     * Create a new inventory item
     */
    async createItem(input: CreateInventoryItemInput): Promise<InventoryItem | null> {
      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot create item: No authenticated user')
          return null
        }

        // Get current member for name
        const membersStore = useMembersStore()
        const currentMember = await membersStore.fetchMember(user.uid)
        const creatorName = currentMember?.name || user.email || 'Unknown'

        const db = getFirestore()
        const itemsCollection = collection(db, 'inventory')

        // Build item data, excluding undefined fields (Firestore rejects undefined)
        const newItem: Record<string, unknown> = {
          category: input.category,
          name: input.name,
          status: 'available',
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          createdBy: user.uid,
          createdByName: creatorName,
          currentAssignment: null,
          assignmentHistory: [],
        }

        // Only add optional fields if they have values
        if (input.description) newItem.description = input.description
        if (input.serialNumber) newItem.serialNumber = input.serialNumber
        if (input.notes) newItem.notes = input.notes

        const docRef = await addDoc(itemsCollection, newItem)

        // Built from the inputs rather than by spreading `newItem`: that is a
        // `Record<string, unknown>` (so Firestore-bound optional fields can be
        // omitted), and spreading it tells TypeScript nothing about the
        // required fields.
        const item: InventoryItem = {
          id: docRef.id,
          category: input.category,
          name: input.name,
          status: 'available',
          createdBy: user.uid,
          createdByName: creatorName,
          ...(input.description ? { description: input.description } : {}),
          ...(input.serialNumber ? { serialNumber: input.serialNumber } : {}),
          ...(input.notes ? { notes: input.notes } : {}),
          created_at: new Date(),
          updated_at: new Date(),
          currentAssignment: null,
          assignmentHistory: [],
        }

        // Update local state
        this.$patch((state) => {
          state.items[docRef.path] = item
        })

        return item
      }
      catch (error) {
        console.error('Error creating inventory item:', error)
        return null
      }
    },

    /**
     * Bulk create inventory items with sequential naming
     * @param input Base item properties
     * @param count Number of items to create
     * @param startNumber Starting number for sequential naming
     * @returns Array of created items and count of failures
     */
    async bulkCreateItems(
      input: CreateInventoryItemInput,
      count: number,
      startNumber: number = 1,
    ): Promise<{ created: InventoryItem[], failed: number }> {
      const created: InventoryItem[] = []
      let failed = 0

      for (let i = 0; i < count; i++) {
        const itemNumber = startNumber + i
        const itemName = `${input.name} #${itemNumber}`

        const item = await this.createItem({
          ...input,
          name: itemName,
        })

        if (item) {
          created.push(item)
        }
        else {
          failed++
        }
      }

      return { created, failed }
    },

    /**
     * One item, live while the caller holds it (utils/liveData.ts)
     */
    async fetchItem(itemId: string): Promise<InventoryItem | null> {
      try {
        await acquireLive(this.itemSpec(itemId))
      }
      catch (error) {
        console.error(`Error fetching inventory item ${itemId}:`, error)
      }
      return this.items[`inventory/${itemId}`] ?? null
    },

    /**
     * Every item, live while the caller holds them (admins hold them from
     * sign-in)
     */
    async fetchItems(): Promise<InventoryItem[]> {
      this.loading = !this.loaded
      try {
        await acquireLive(this.allItemsSpec())
      }
      catch (error) {
        console.error('Error fetching inventory items:', error)
      }
      finally {
        this.loading = false
      }
      return this.itemsArray
    },

    allItemsSpec(): LiveSpec<InventoryItemFirestoreData> {
      return {
        key: 'inventory',
        covers: 'inventory',
        listen: (emit, fail) => onSnapshot(query(collection(getFirestore(), 'inventory'), orderBy('name')), ...liveQueryListener('inventory', emit, fail)),
        apply: changes => this.applyItems(changes),
        evict: paths => this.evictItems(paths),
        ready: () => {
          this.loaded = true
        },
        stopped: () => {
          this.loaded = false
        },
      }
    },

    itemSpec(itemId: string): LiveSpec<InventoryItemFirestoreData> {
      const path = `inventory/${itemId}`
      return {
        key: path,
        within: 'inventory',
        listen: (emit, fail) => onSnapshot(doc(getFirestore(), 'inventory', itemId), ...liveDocListener(path, emit, fail)),
        apply: changes => this.applyItems(changes),
        evict: paths => this.evictItems(paths),
      }
    },

    memberItemsSpec(memberId: string): LiveSpec<InventoryItemFirestoreData> {
      return {
        key: `inventory?assignedTo=${memberId}`,
        within: 'inventory',
        listen: (emit, fail) => onSnapshot(
          query(
            collection(getFirestore(), 'inventory'),
            where('status', '==', 'assigned'),
            where('currentAssignment.memberId', '==', memberId),
          ),
          ...liveQueryListener('inventory', emit, fail),
        ),
        apply: changes => this.applyItems(changes),
        evict: paths => this.evictItems(paths),
      }
    },

    applyItems(changes: LiveChange<InventoryItemFirestoreData>[]) {
      this.$patch((state) => {
        for (const { path, data } of changes) {
          state.items[path] = this.convertToItem(path.slice('inventory/'.length), data!)
        }
      })
    },

    evictItems(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.items[path]
      })
    },

    /**
     * Assign an item to a member
     */
    async assignItem(input: AssignInventoryItemInput): Promise<boolean> {
      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot assign item: No authenticated user')
          return false
        }

        const db = getFirestore()
        const itemRef = doc(db, 'inventory', input.itemId)
        const itemPath = `inventory/${input.itemId}`

        // Get the item
        const item = this.items[itemPath] || (await this.fetchItem(input.itemId))
        if (!item) {
          console.error('Item not found:', input.itemId)
          return false
        }

        if (item.status === 'assigned') {
          console.error('Item is already assigned:', input.itemId)
          return false
        }

        // Get member info
        const membersStore = useMembersStore()
        const member = await membersStore.fetchMember(input.memberId)
        if (!member) {
          console.error('Member not found:', input.memberId)
          return false
        }

        // Get current user's name
        const currentMember = await membersStore.fetchMember(user.uid)
        const assignerName = currentMember?.name || user.email || 'Unknown'

        const assignment: InventoryAssignment = {
          memberId: input.memberId,
          memberName: member.name,
          assignedAt: new Date(),
          assignedBy: user.uid,
          assignedByName: assignerName,
          ...(input.notes && { notes: input.notes }),
        }

        // Build currentAssignment for Firestore (without undefined values)
        const firestoreAssignment: Record<string, unknown> = {
          memberId: input.memberId,
          memberName: member.name,
          assignedAt: serverTimestamp(),
          assignedBy: user.uid,
          assignedByName: assignerName,
        }
        if (input.notes) {
          firestoreAssignment.notes = input.notes
        }

        // Build history entry with regular Date (serverTimestamp not allowed in arrays)
        const historyEntry: Record<string, unknown> = {
          memberId: input.memberId,
          memberName: member.name,
          assignedAt: new Date(),
          assignedBy: user.uid,
          assignedByName: assignerName,
        }
        if (input.notes) {
          historyEntry.notes = input.notes
        }

        // Update Firestore
        await updateDoc(itemRef, {
          status: 'assigned',
          currentAssignment: firestoreAssignment,
          assignmentHistory: [
            ...(item.assignmentHistory || []),
            historyEntry,
          ],
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          state.items[itemPath] = {
            ...item,
            status: 'assigned',
            currentAssignment: assignment,
            assignmentHistory: [...(item.assignmentHistory || []), assignment],
            updated_at: new Date(),
          }
        })

        return true
      }
      catch (error) {
        console.error('Error assigning inventory item:', error)
        return false
      }
    },

    /**
     * Bulk assign multiple items to a single member (captain)
     */
    async bulkAssignItems(input: {
      itemIds: string[]
      memberId: string
      notes?: string
    }): Promise<{ assigned: number, failed: number }> {
      let assigned = 0
      let failed = 0

      for (const itemId of input.itemIds) {
        const success = await this.assignItem({
          itemId,
          memberId: input.memberId,
          notes: input.notes,
        })

        if (success) {
          assigned++
        }
        else {
          failed++
        }
      }

      return { assigned, failed }
    },

    /**
     * Return an item (unassign from member)
     */
    async returnItem(input: ReturnInventoryItemInput): Promise<boolean> {
      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot return item: No authenticated user')
          return false
        }

        const db = getFirestore()
        const itemRef = doc(db, 'inventory', input.itemId)
        const itemPath = `inventory/${input.itemId}`

        // Get the item
        const item = this.items[itemPath] || (await this.fetchItem(input.itemId))
        if (!item) {
          console.error('Item not found:', input.itemId)
          return false
        }

        if (item.status !== 'assigned' || !item.currentAssignment) {
          console.error('Item is not assigned:', input.itemId)
          return false
        }

        // Get current user's name
        const membersStore = useMembersStore()
        const currentMember = await membersStore.fetchMember(user.uid)
        const returnerName = currentMember?.name || user.email || 'Unknown'

        // Update the last assignment in history with return info
        const updatedHistory = [...(item.assignmentHistory || [])]
        const lastAssignment = updatedHistory[updatedHistory.length - 1]
        if (lastAssignment) {
          lastAssignment.returnedAt = new Date()
          lastAssignment.returnedBy = user.uid
          lastAssignment.returnedByName = returnerName
          if (input.notes) {
            lastAssignment.notes = `${lastAssignment.notes || ''} | Return: ${input.notes}`.trim()
          }
        }

        // Update Firestore (use regular Date for array items, serverTimestamp not allowed)
        await updateDoc(itemRef, {
          status: 'available',
          currentAssignment: null,
          assignmentHistory: updatedHistory,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          state.items[itemPath] = {
            ...item,
            status: 'available',
            currentAssignment: null,
            assignmentHistory: updatedHistory,
            updated_at: new Date(),
          }
        })

        return true
      }
      catch (error) {
        console.error('Error returning inventory item:', error)
        return false
      }
    },

    /**
     * Update item status (lost, damaged, retired)
     */
    async updateItemStatus(itemId: string, status: InventoryStatus, notes?: string): Promise<boolean> {
      try {
        const db = getFirestore()
        const itemRef = doc(db, 'inventory', itemId)
        const itemPath = `inventory/${itemId}`

        const item = this.items[itemPath]
        if (!item) {
          console.error('Item not found:', itemId)
          return false
        }

        const updateData: Partial<InventoryItemFirestoreData> = {
          status,
          updated_at: serverTimestamp(),
        }

        if (notes) {
          updateData.notes = notes
        }

        // If marking as lost/damaged/retired while assigned, clear assignment
        if (status !== 'available' && status !== 'assigned' && item.currentAssignment) {
          updateData.currentAssignment = null
        }

        await updateDoc(itemRef, updateData)

        this.$patch((state) => {
          state.items[itemPath] = {
            ...item,
            status,
            notes: notes || item.notes,
            currentAssignment: status !== 'available' && status !== 'assigned' ? null : item.currentAssignment,
            updated_at: new Date(),
          }
        })

        return true
      }
      catch (error) {
        console.error('Error updating item status:', error)
        return false
      }
    },

    /**
     * Update an inventory item
     */
    async updateItem(itemId: string, updates: Partial<CreateInventoryItemInput>): Promise<boolean> {
      try {
        const db = getFirestore()
        const itemRef = doc(db, 'inventory', itemId)
        const itemPath = `inventory/${itemId}`

        const item = this.items[itemPath]
        if (!item) {
          console.error('Item not found:', itemId)
          return false
        }

        // Build update data without undefined values
        const updateData: Record<string, unknown> = {
          updated_at: serverTimestamp(),
        }

        if (updates.category !== undefined) {
          updateData.category = updates.category
        }
        if (updates.name !== undefined) {
          updateData.name = updates.name
        }
        if (updates.description !== undefined) {
          updateData.description = updates.description || deleteField()
        }
        if (updates.serialNumber !== undefined) {
          updateData.serialNumber = updates.serialNumber || deleteField()
        }
        if (updates.notes !== undefined) {
          updateData.notes = updates.notes || deleteField()
        }

        await updateDoc(itemRef, updateData)

        // Build local update without undefined
        const localUpdates: Partial<InventoryItem> = {}
        if (updates.category) localUpdates.category = updates.category
        if (updates.name) localUpdates.name = updates.name
        localUpdates.description = updates.description || undefined
        localUpdates.serialNumber = updates.serialNumber || undefined
        localUpdates.notes = updates.notes || undefined

        this.$patch((state) => {
          state.items[itemPath] = {
            ...item,
            ...localUpdates,
            updated_at: new Date(),
          }
        })

        return true
      }
      catch (error) {
        console.error('Error updating inventory item:', error)
        return false
      }
    },

    /**
     * Delete an inventory item
     */
    async deleteItem(itemId: string): Promise<boolean> {
      try {
        const db = getFirestore()
        const itemRef = doc(db, 'inventory', itemId)
        const itemPath = `inventory/${itemId}`

        await deleteDoc(itemRef)

        this.$patch((state) => {
          delete state.items[itemPath]
        })

        return true
      }
      catch (error) {
        console.error('Error deleting inventory item:', error)
        return false
      }
    },

    /**
     * The items assigned to a member, live while the caller holds them
     */
    async fetchItemsForMember(memberId: string): Promise<InventoryItem[]> {
      try {
        await acquireLive(this.memberItemsSpec(memberId))
      }
      catch (error) {
        console.error(`Error fetching items for member ${memberId}:`, error)
      }
      return this.itemsByMemberId(memberId)
    },

    /**
     * Generate QR code data for an item
     */
    generateQRData(itemId: string): string {
      return `inventory:${itemId}`
    },

    /**
     * Parse QR code data
     */
    parseQRData(data: string): { type: 'inventory' | 'checkin' | 'unknown', id: string } {
      if (data.startsWith('inventory:')) {
        return { type: 'inventory', id: data.substring(10) }
      }
      if (data.startsWith('checkin:')) {
        return { type: 'checkin', id: data.substring(8) }
      }
      // Assume raw UID for backwards compatibility with member QR codes
      return { type: 'checkin', id: data }
    },
  },
})
